import { useEffect } from "react";
import { Loader2, Pause, Play, SkipBack } from "lucide-react";
import WaveCanvas from "@/components/recstudio/WaveCanvas";
import { VERSIONS, fmtTime } from "@/lib/enhanceApi";

function VersionTile({ v, meta, selected, loaded, onPick }) {
  return (
    <button type="button" data-testid={`compare-pick-${v.key}`} disabled={!meta} onClick={() => onPick(v.key)}
      aria-pressed={selected}
      className={`relative rounded-2xl border p-4 text-left transition-[transform,background-color,border-color] duration-200 disabled:cursor-not-allowed disabled:opacity-40 ${selected ? "-translate-y-0.5 bg-white/[0.07]" : "border-white/10 bg-black/30 hover:bg-white/[0.05]"}`}
      style={selected ? { borderColor: v.color, boxShadow: `0 0 0 1px ${v.color}55, 0 10px 40px -12px ${v.color}88` } : undefined}>
      <div className="flex items-center justify-between">
        <span className="grid h-8 w-8 place-items-center rounded-lg font-mono text-[13px] font-bold text-black" style={{ background: v.color }}>{v.short}</span>
        {meta && !loaded ? <Loader2 className="h-4 w-4 animate-spin text-white/40" /> : null}
        {meta && loaded ? <span className="font-mono text-[10px] text-white/45">{meta.lufs} LUFS</span> : null}
      </div>
      <div className="mt-3 font-display text-[15px] font-semibold">{v.label}</div>
      <div className="mt-0.5 text-[12px] leading-snug text-white/50">{meta ? v.note : "Working on it…"}</div>
    </button>
  );
}

/** Original vs Natural vs Studio-Polished on one clock — press a version (or 1 / 2 / 3) to switch without losing your place. */
export default function ComparePlayer({ item, cmp }) {
  const { buffers, failed, active, select, playing, toggle, seek, pos, duration } = cmp;
  useEffect(() => {
    const onKey = (e) => {
      if (/input|textarea|select/i.test(e.target.tagName) || e.target.isContentEditable) return;
      const v = VERSIONS[Number(e.key) - 1];
      if (v && item.versions?.[v.key]) select(v.key);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [item, select]);
  const color = VERSIONS.find((v) => v.key === active)?.color;
  const ready = Object.keys(buffers).length > 0;
  return (
    <section data-testid="compare-player" className="rounded-3xl border border-white/[0.08] bg-[#0a0d1c]/80 p-5 sm:p-6">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-display text-base font-semibold md:text-lg">Compare, before and after</h2>
        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-white/40">All versions play in sync · keys 1 2 3</span>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        {VERSIONS.map((v) => (
          <VersionTile key={v.key} v={v} meta={item.versions?.[v.key]} selected={active === v.key} loaded={Boolean(buffers[v.key])} onPick={select} />
        ))}
      </div>
      <div className="mt-5 rounded-2xl border border-white/[0.06] bg-black/40 p-3">
        {ready ? (
          <WaveCanvas buffer={buffers[active] || Object.values(buffers)[0]} duration={duration} playhead={pos} onSeek={seek} height={84} color={color} testId="compare-wave" />
        ) : (
          <div className="flex h-[84px] items-center justify-center gap-2 text-[13px] text-white/45"><Loader2 className="h-4 w-4 animate-spin" /> Loading the versions…</div>
        )}
      </div>
      {failed ? <p data-testid="compare-load-error" className="mt-2 text-[12px] text-rose-300">One version didn't load — refresh the page to try again.</p> : null}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button type="button" data-testid="compare-restart" onClick={() => seek(0)} disabled={!ready} aria-label="Back to start"
          className="grid h-11 w-11 place-items-center rounded-full border border-white/15 bg-white/[0.06] text-white/80 transition-colors hover:bg-white/[0.14] disabled:opacity-40">
          <SkipBack className="h-4 w-4" />
        </button>
        <button type="button" data-testid="compare-play" onClick={toggle} disabled={!ready} aria-label={playing ? "Pause" : "Play"}
          className="grid h-14 w-14 place-items-center rounded-full text-black shadow-lg transition-transform hover:scale-105 disabled:opacity-40" style={{ background: color }}>
          {playing ? <Pause className="h-6 w-6" /> : <Play className="ml-0.5 h-6 w-6" />}
        </button>
        <span data-testid="compare-time" className="font-mono text-[13px] tabular-nums text-white/70">{fmtTime(pos)} / {fmtTime(duration)}</span>
        <span data-testid="compare-now-playing" className="w-full font-mono text-[11px] uppercase tracking-[0.16em] sm:ml-auto sm:w-auto" style={{ color }}>
          Hearing: {VERSIONS.find((v) => v.key === active)?.label}
        </span>
      </div>
    </section>
  );
}
